/**
 * The "Value-Added Benefits" section of an industry page: an introduction and a call-to-action
 * button, plus one card per benefit. Below `lg` it renders the introduction, a
 * `./Mobile/ValueAddedBenefitsMobile.tsx` card per benefit and a full-width button itself; from
 * `lg` up it renders `./Desktop/ValueAddedBenefitsDesktop.tsx` (image cards that reveal the
 * description on hover or click).
 *
 * Position: the first `AllSections` entry on every Ryerson industry page under
 * `src/pages/metal-solutions/industries/` and on the dormant STS pages under
 * `src/pages/metal-solutions/industries-served/`. Each passes one language of its content module's
 * benefits object (`ValueAddedBenefitsContent` on the Ryerson pages, for example
 * `TechnologyValueAddedBenefitsContent` on STS), typed with {@link ValueAddedBenefitsProperties}.
 */
import React from "react";
import { useTheme } from "@ryerson/frontend.theme";
import { Media } from "@ryerson/frontend.next";
import { ContentSection } from "@ryerson/frontend.layout";
import styled from "@emotion/styled";
import { Typography } from "@ryerson/frontend.typography";
import { Button } from "@ryerson/frontend.button";
import ValueAddedBenefitsMobile from "./Mobile/ValueAddedBenefitsMobile";
import ValueAddedBenefitsDesktop from "./Desktop/ValueAddedBenefitsDesktop";
import { navigate } from "@ryerson/frontend.next";

/** One benefit card. */
export type SecondaryContent = {
	title: string;
	description: string;
	/** Card image: desktop covers the card with it until hover or click; mobile shows it above. */
	imageUrl: string;
	/** Not read: the desktop card always has its image. */
	showImageOnDesktop: boolean;
	/** Mobile only: whether the card shows `imageUrl`. */
	showImageOnMobile: boolean;
};

/** Copy for one language of the section; content modules type their objects with it. */
export interface ValueAddedBenefitsProperties {
	/** Desktop heading. Pages also use it as the `AllSections` title (and anchor). */
	title: string;
	/** Introductory paragraph. */
	mainDescription: string;
	/** Benefit cards, in display order. Desktop lays them in one row of 360 px cards. */
	secondaryContent: SecondaryContent[];
	buttonLabel: string;
	/** Where the button goes. Default `/contact-us`. */
	buttonUrl?: string;
}

const ValueBenefitsDescription = styled.div`
	margin: 0px 0px 35px 0px;
`;

const ButtonContainer = styled.div`
	margin: 40px 0px 26px 0px;
`;

/** Renders the section for the current breakpoint; see the file header. */
const ValueAddedBenefits: React.FC<ValueAddedBenefitsProperties> = (props) => {
	const { theme } = useTheme();

	const buttonLink = props.buttonUrl ? props.buttonUrl : "/contact-us";

	return (
		<>
			<Media lessThan="lg">
				<ContentSection type="primary" vPadding="34px" hPadding="0px">
					<ValueBenefitsDescription>
						<Typography color={theme.colors.primary.gray}>
							{props.mainDescription}
						</Typography>
					</ValueBenefitsDescription>
					{props.secondaryContent.map((content: SecondaryContent, index: number) => (
						<ValueAddedBenefitsMobile
							key={index}
							id={index}
							textColor={theme.colors.primary.lightGray}
							backgroundColor={theme.colors.primary.secondaryBrand}
							content={content}
						/>
					))}
					<ButtonContainer>
						<Button
							label={props.buttonLabel}
							onClick={() => {
								navigate(buttonLink);
							}}
							background={theme.colors.primary.accentBrand}
							fullwidth={true}
						/>
					</ButtonContainer>
				</ContentSection>
			</Media>
			<Media greaterThanOrEqual="lg">
				<ValueAddedBenefitsDesktop {...props} />
			</Media>
		</>
	);
};

export default ValueAddedBenefits;
